import type {
  TableView,
  TableViewFilters,
  TableViewGetItemQueryKeyProps,
  TableViewParams,
  TableViewQueryParams,
  TableViewSort,
} from "./table-view.type";
import type { QueryKey } from "@tanstack/react-query";

import { QUERY_PERSIST_KEY } from "@/services/query";
import { normalizeArrayOfString } from "@/utilities/array";
import { isValidObject } from "@/utilities/object";

const BASE_QUERY_KEY = ["users", "current", "table-views"];

export const getListQueryKey = (
  params: TableViewQueryParams = {}
): QueryKey => {
  return [...BASE_QUERY_KEY, "list", params];
};

export const getItemQueryKey = ({
  tableKey,
  params,
  persist,
}: TableViewGetItemQueryKeyProps): QueryKey => {
  const queryKey = [...BASE_QUERY_KEY, tableKey];
  const persistKey = persist ? [QUERY_PERSIST_KEY] : [];

  if (!params) return [...queryKey, ...persistKey];

  return [...queryKey, params, ...persistKey];
};

export const normalizeTableViews = (tableViews: TableView[]) => {
  return tableViews.map(normalizeTableView);
};

export const normalizeTableView = (tableView: TableView): TableView => {
  return {
    ...tableView,
    parameters: normalizeTableViewParams(tableView.parameters),
  };
};

export const normalizeTableViewParams = (
  parameters: TableViewParams
): TableViewParams => {
  const { sort, filters, pageSize } = parameters ?? {};

  return {
    sort: normalizeTableViewSort(sort),
    filters: normalizeTableViewFilters(filters),
    pageSize,
  };
};

const normalizeTableViewSort = (sort: TableViewSort): TableViewSort => {
  return normalizeArrayOfString(sort);
};

const normalizeTableViewFilters = (
  filters: TableViewFilters
): TableViewFilters => {
  if (!isValidObject(filters)) return {};

  const normalizedFilters: TableViewFilters = {};

  Object.entries(filters).forEach(([filterKey, filterValue]) => {
    const values = normalizeArrayOfString(filterValue);
    normalizedFilters[filterKey] = values;
  });

  return normalizedFilters;
};
